/****************************************************************
 * File: [caseid].page.tsx
 * Description:
 * Author: zayfen <zhangyunfeng@pudutech.com>
 * Created: 2022/05/26 19:25
 *
 * History:
 *   Date               Programmer ID    Description
 *  -----------------  ---------------  ------------------------
 *  2022/05/26 19:25   zayfen           Init
 *
 * This unpublished material is proprietary to PuduTech.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of PuduTech.
 *****************************************************************/

import { GetStaticPaths, GetStaticPathsContext, GetStaticPropsContext } from 'next'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { NextPageWithLayout } from '../../common/common-types'
import PageLayout from '../../components/page-layout/page-layout'
import retriveMessages, { LocaleType } from '../../messages/message'
import { PrimaryLocales, useChinaDetector } from '../../common/region-locale'
import { PdContactUsBanner } from '../../components/pd-contactus-banner/pd-contactus-banner'
import useIframeMessage from '../../hooks/use-iframe-message'
import PdFloatRobot from '../../components/pd-float-robot/pd-float-robot'
import { useTranslations } from 'next-intl'
import { getProductMeta } from '../../common/seo'
import { EventReportManager } from '../../event-report/event-report-manager'
import useSWR from 'swr'
import { getProductBaseData, getProductDetailData, ProductBaseData, ProductDetailData } from '@/service/product'
import { MetaPage } from '@/common/meta-component-mgmt'
import { withSEO } from '@/common/with-seo'

const ConfigMap: Record<string, { src: string; bgColor: string; color: string }> = {
  // 清洁
  puduCC1: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/puduCC1-float.png',
    bgColor: '#c0df16',
    color: '#000',
  },
  sh: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/sh-float.png',
    bgColor: '#c0df16',
    color: '#000',
  },
  // 配送
  flashbot: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/flashbot-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  pudut300: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/pudut300-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  kettybot: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/kettybot-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  kettybot_pro: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/kettybot_pro-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  pudubot: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/pudubot-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  pudubot_vslam: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/pudubot-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  bellabot: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/bellabot-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  holabot: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/holabot-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  pudubot2: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/pudubot2-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  swiftbot: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/swiftbot-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
  bellabotpro: {
    src: 'https://cdn.pudutech.com/official-website/product/float_btn/bellabotpro-float.png',
    bgColor: '#0066ff',
    color: '#fff',
  },
}

const RobotDetails: NextPageWithLayout<{ productBaseData: ProductBaseData; productDetailData: ProductDetailData }> = (
  props
) => {
  const router = useRouter()
  const { robot } = router.query as { robot: string }
  const locale: string = router.locale as string
  const inChina = useChinaDetector()
  const [hostname, setHostName] = useState<string>('')
  const t = useTranslations()
  const reported = useRef(false)

  useEffect(() => {
    setHostName(window.location.hostname)
  }, [])

  useEffect(() => {
    const meta = getProductMeta(router.asPath)

    if (meta && !reported.current) {
      reported.current = true
      EventReportManager.getInstance().fireEventImmediately(
        {
          data: {
            event: 'view_item',
            item_id: meta.id,
            item_name: meta.name,
            item_category: meta.category,
          },
        },
        true
      )
    }
  }, [])

  const robotDetailsCallback = useCallback(() => {
    if (hostname) {
      const isTestDomain = hostname.startsWith('alpha.pudutech.com') || hostname === 'localhost'
      const PRODBASE: string = inChina ? `https://old-official.pudutech.com` : `https://old-official.pudurobotics.com`
      const TESTBASE: string = inChina ? `https://t.pudutech.com` : `https://t.pudurobotics.com`
      // todo: change
      // const BASE: string = isTestDomain ? TESTBASE : PRODBASE
      const BASE: string = PRODBASE
      let localeCode = !PrimaryLocales.includes(locale) ? locale.substring(0, 2) : ''

      // 葫芦Pro:中文繁体、韩语、德语、西班牙语、意大利语、法语，以上语种接入葫芦Pro（产品下拉列表及产品详情）
      if (robot === 'kettybot_pro' && !!localeCode && !['en', 'jp'].includes(localeCode)) {
        localeCode = 'en'
      }

      return [BASE, localeCode, `product/detail/${robot}?hideHeaderFooter=1`].filter((p) => !!p).join('/')
    }
  }, [robot, inChina, hostname])

  // 不同配置
  const config = useMemo(() => {
    const result = ConfigMap[robot]
    return result || ConfigMap['kettybot_pro']
  }, [robot])

  // iframe消息通信
  useIframeMessage()

  useEffect(() => {
    if (robot === 'bellabot_industrial_version' && typeof window !== 'undefined') {
      router.replace({ pathname: '/404' })
    }
  }, [])

  const { data: productBaseData } = useSWR(getProductBaseData.swrkey(router.locale || '', robot), () =>
    getProductBaseData(robot, locale)
  )

  const finalProductBaseData = props.productBaseData

  return (
    <>
      {finalProductBaseData?.useDescription ? (
        <MetaPage productId={robot} initialData={props.productDetailData} />
      ) : (
        <div className="w-full h-fit">
          <PdFloatRobot
            robot={config.src}
            color={config.bgColor}
            hideCircleBorder={true}
            buttonText={t('RequestDemo')}
            buttonTextSize="small"
            buttonTextColor={config.color}
          />
          <iframe
            id="product-details"
            className="w-screen min-h-screen h-fit overflow-hidden border-0"
            src={robotDetailsCallback()}
          >
            Browser not compatible
          </iframe>
          <PdContactUsBanner />
        </div>
      )}
    </>
  )
}

export const getStaticProps = withSEO(
  async ({ locale, params }: GetStaticPropsContext) => {
    const messages = retriveMessages(['products'], locale as LocaleType)
    const { robot } = params as { robot: string }

    console.log('locale: ', locale)

    const [productBaseData, productDetailData] = await Promise.all([
      getProductBaseData(robot, locale),
      getProductDetailData(robot, locale),
    ])
    console.log('productBaseData: ', productBaseData)

    return {
      props: {
        messages,
        productBaseData: productBaseData || {},
        productDetailData: productDetailData || [],
      },
      revalidate: 60,
    }
  },
  ({ params }: GetStaticPropsContext) => {
    const { robot } = params as { robot: string }
    return `/products/${robot}` // 动态路由路径
  }
)

export const getStaticPaths: GetStaticPaths<{ robot: string }> = async (context: GetStaticPathsContext) => {
  const buildPath = (robot: string) => context.locales?.map((locale) => `/${locale}/products/${robot}`) || []
  return {
    paths: [
      ...buildPath('bellabot'),
      ...buildPath('swiftbot'),
      ...buildPath('pudubot'),
      ...buildPath('holabot'),
      ...buildPath('kettybot'),
      ...buildPath('flashbot'),
      ...buildPath('puduA1'),
      ...buildPath('puduD1'),
      ...buildPath('puduSH1'),
      ...buildPath('puduCC1'),
      ...buildPath('mt1-vac'),
    ],
    fallback: 'blocking', // means will server-render pages on-demand if the path doesn't exist
  }
}

RobotDetails.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      bgWhite={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}

export default RobotDetails
